import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

export interface AcceptedBadge {
  /** "30009:<pubkey>:<d>" */
  badgeRef: string;
  /** Badge Award event id */
  awardEventId: string;
}

export interface ProfileBadges {
  event: NostrEvent | undefined;
  accepted: AcceptedBadge[];
}

function parseProfileBadges(event: NostrEvent): AcceptedBadge[] {
  const accepted: AcceptedBadge[] = [];
  const tags = event.tags;

  for (let i = 0; i < tags.length - 1; i++) {
    if (tags[i][0] === 'a' && tags[i + 1][0] === 'e') {
      accepted.push({
        badgeRef: tags[i][1],
        awardEventId: tags[i + 1][1],
      });
      i++; // skip the paired 'e' tag
    }
  }

  return accepted;
}

/** Fetch profile badges (kind 10008) for a specific pubkey */
export function useProfileBadges(pubkey: string | undefined) {
  const { nostr } = useNostr();

  return useQuery<ProfileBadges>({
    queryKey: ['nostr', 'profile-badges', pubkey ?? ''],
    queryFn: async (c) => {
      if (!pubkey) return { event: undefined, accepted: [] };

      const [event] = await nostr.query(
        [{ kinds: [10008], authors: [pubkey], limit: 1 }],
        { signal: c.signal },
      );

      if (!event) return { event: undefined, accepted: [] };

      return {
        event,
        accepted: parseProfileBadges(event),
      };
    },
    enabled: !!pubkey,
    staleTime: 60_000,
  });
}
